import React, { useCallback, useState } from "react";
import TextField from "@mui/material/TextField";
import apiClient from "../api/apiClient";
import Delete from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { handleErrors } from "../modules/handleErrors";
import LoadingSpinner from "../components/LoadingSpinner";
import InfiniteScroll from "../components/InfiniteScroll";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";

const PAGE_SIZE = 12;

const AdminFeatureMockup = () => {
  const [mockupIds, setMockupIds] = useState("");
  const [loadersubmit, setloadersubmit] = useState(false);

  const fetchFeaturedPage = useCallback(async (page) => {
    return apiClient.get(
      `/featured-listings/?page=${page}&page_size=${PAGE_SIZE}`,
    );
  }, []);

  const {
    data: FeaturedMockupscard,
    loading,
    hasMore,
    loadMore,
    refetch,
  } = useInfiniteScroll(fetchFeaturedPage, [], PAGE_SIZE);

  // Parse comma-separated IDs and keep them as strings (preserving leading zeros)
  const parseIds = (idsString) => {
    if (!idsString.trim()) return [];
    return idsString
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
  };

  const postfeature = async () => {
    const listid = parseIds(mockupIds);

    if (listid.length === 0) {
      toast.error("Please enter valid mockup IDs");
      return;
    }

    setloadersubmit(true);
    try {
      const result = await apiClient.post(`/featured-listings/`, {
        listing_ids: listid,
      });
      setloadersubmit(false);

      if (!result.ok) {
        handleErrors(result.data);
        return;
      }

      toast.success("Mockups successfully featured!");
      setMockupIds(""); // Clear the input
      refetch();
    } catch (error) {
      setloadersubmit(false);
      console.error("Error in postfeature:", error);
      toast.error("Failed to feature mockups");
    }
  };

  const handlefeature = async (id) => {
    const result = await apiClient.delete(`/featured-listings/${id}/`);

    if (!result.ok) return handleErrors(result.data);
    toast.success("Mockup successfully removed from featured");
    refetch();
  };

  const showInitialSpinner = loading && FeaturedMockupscard.length === 0;

  return (
    <>
      <div className="">
        <div className="container">
          <div className="d-flex flex-column gap-3 my-3">
            <div>
              <h4>Add Featured Mockups</h4>
              <p className="text-muted">
                Enter mockup IDs separated by commas (e.g., 000123, 000456,
                000789)
              </p>
              <p className="text-muted">
                <small>
                  Note: Enter IDs exactly as they appear in the system,
                  including any leading zeros.
                </small>
              </p>
            </div>

            <div className="d-flex gap-2 align-items-end flex-wrap">
              <TextField
                size="small"
                label="Mockup IDs"
                variant="outlined"
                value={mockupIds}
                onChange={(e) => setMockupIds(e.target.value)}
                placeholder="Enter mockup IDs separated by commas..."
                style={{ width: 400, maxWidth: "100%" }}
                multiline
                rows={2}
              />
              <div>
                <button
                  className="btn text-light"
                  style={{ backgroundColor: "#C93312" }}
                  onClick={postfeature}
                  disabled={loadersubmit}
                >
                  {loadersubmit ? "Adding..." : "Add to Featured"}
                </button>
              </div>
            </div>

            {mockupIds && (
              <div className="mt-2">
                <small className="text-muted">
                  Preview: {parseIds(mockupIds).length} mockup(s) will be
                  featured
                  {parseIds(mockupIds).length > 0 &&
                    ` (IDs: ${parseIds(mockupIds).join(", ")})`}
                </small>
              </div>
            )}
          </div>

          <div className="row">
            <div className="col-12">
              <h4>Currently Featured Mockups</h4>
              {showInitialSpinner ? (
                <div className="d-flex justify-content-center py-5">
                  <LoadingSpinner color="#FF3E1F" />
                </div>
              ) : FeaturedMockupscard.length === 0 ? (
                <p className="text-muted">
                  No mockups are currently featured.
                </p>
              ) : (
                <InfiniteScroll
                  onLoadMore={loadMore}
                  hasMore={hasMore}
                  loading={loading}
                  loader={<LoadingSpinner color="#FF3E1F" />}
                >
                  <div className="row">
                    {FeaturedMockupscard.map((item) => (
                      <div
                        className=" col-6 col-lg-3 mb-3"
                        key={item?.id}
                      >
                        <div
                          className="card p-2 shadow h-100"
                          style={{ borderTopRightRadius: "30px" }}
                        >
                          <div className="card-img-wrapper">
                            <div className="" style={{ width: "18rem" }}>
                              <Delete
                                onClick={() => handlefeature(item?.id)}
                                style={{
                                  position: "absolute",
                                  zIndex: "99",
                                  cursor: "pointer",
                                  color: "#dc3545",
                                }}
                                titleAccess="Remove from featured"
                              />
                              <img
                                src={
                                  item?.listing?.use_main_file_as_thumbnail
                                    ? item?.listing?.compressed_file
                                    : item?.thumbnails?.[0]?.image
                                }
                                className="card-img-top"
                                alt="Product"
                                style={{
                                  width: "100%",
                                  height: "220px",
                                  borderTopRightRadius: "30px",
                                  objectFit: "cover",
                                  objectPosition: "center",
                                }}
                              />
                            </div>
                          </div>
                          <div className="card-body text-center">
                            <h6 className="card-title text-start mb-1 testelips">
                              {item?.listing?.title}
                            </h6>
                            <small className="text-muted">
                              ID: {item?.listing?.id}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </InfiniteScroll>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminFeatureMockup;
